import { useEffect, useRef, useState } from 'react';

import { extractSmartLinksFromAdf } from '@confluence/connectors/entry-points/extractSmartLinksFromAdf.ts';
import { chooseMostReferencedProduct } from '@confluence/experiment-related-content-connections/entry-points/chooseMostReferencedProduct.ts';
import type {
	QualifyingProduct,
	ThirdPartyPromptState,
} from '@confluence/experiment-related-content-connections/entry-points/thirdPartyPromptTypes.ts';
import { useRendererActions } from '@confluence/renderer-actions/entry-points/RendererActionsContext.ts';

// The renderer may never mount, so the slot resolves after a generous wait rather than holding the
// skeleton for ever. Long enough that a normal load always wins the race. Copies useSmartLinks.ts:97
const ADF_WAIT_TIMEOUT_MS = 10000;

export type ThirdPartyPromptSlotResult = {
	/** Undefined once resolved means the slot goes back to the first-party card. */
	product: QualifyingProduct | undefined;
	isResolved: boolean;
};

const UNRESOLVED: ThirdPartyPromptSlotResult = { product: undefined, isResolved: false };
const NO_SLOT: ThirdPartyPromptSlotResult = { product: undefined, isResolved: true };

/**
 * Which product fills the reserved slot. Unresolved during server render and on the first client
 * render, so both sides show the skeleton and hydration matches.
 */
export const useThirdPartyPromptProduct = (
	state: ThirdPartyPromptState,
): ThirdPartyPromptSlotResult => {
	const { pageAdf } = useRendererActions();
	const [slot, setSlot] = useState<ThirdPartyPromptSlotResult>(UNRESOLVED);

	// Detection runs on every render, so its result is read through a ref. Keeping it out of the deps
	// is what stops an ancestor re-render, on scroll for example, re-walking the document and flashing
	// the skeleton back. Follows useSmartLinks.ts:81.
	const stateRef = useRef(state);
	stateRef.current = state;

	const hasResolvedRef = useRef(false);
	const { kind } = state;

	useEffect(() => {
		if (hasResolvedRef.current || kind === 'none') {
			return;
		}

		const resolve = (product?: QualifyingProduct) => {
			hasResolvedRef.current = true;
			setSlot({ product, isResolved: true });
		};

		const current = stateRef.current;

		// One candidate leaves the chooser no work, so the slot fills without waiting for the document.
		if (current.kind === 'single') {
			resolve(current.product);
			return;
		}

		if (!pageAdf) {
			const timeoutId = setTimeout(() => resolve(undefined), ADF_WAIT_TIMEOUT_MS);
			return () => clearTimeout(timeoutId);
		}

		// `true` includes link marks. Without it the walk finds card nodes only and misses every plain
		// hyperlink, which is the whole reason this feature needs the walk.
		const links = extractSmartLinksFromAdf(pageAdf, true);

		resolve(
			current.kind === 'many' ? chooseMostReferencedProduct(current.candidates, links) : undefined,
		);
	}, [kind, pageAdf]);

	return kind === 'none' ? NO_SLOT : slot;
};
